// Sendezeiten: Wochenraster (So … Sa × 0–23 Uhr) zum Freigeben/Sperren, dazu ein optionaler Zeitraum.
// Gespeichert als 7 Zahlen (Bit h = Stunde h erlaubt) oder null (= immer).

import { useEffect, useState } from "react";
import { Field, Modal } from "./ui";

const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const IDX = [1, 2, 3, 4, 5, 6, 0]; // display Monday first, stored Sunday first
const ALL = 0xffffff;

export interface Timing { schedule: number[] | null; valid_from: string | null; valid_to: string | null }

const dateStr = (v: any) => (v ? String(v).slice(0, 10) : "");

/** Short text for lists, e.g. "Sa, So" or "Mo–Fr 6–10 Uhr" or "immer". */
export function timingText(t: Partial<Timing> | null | undefined): string {
  const parts: string[] = [];
  const s = Array.isArray(t?.schedule) && t!.schedule.length === 7 ? t!.schedule : null;
  if (s) {
    const open = IDX.filter((d) => s[d]);
    if (!open.length) parts.push("gesperrt");
    else {
      const full = open.every((d) => s[d] === ALL);
      parts.push(open.map((d) => DAYS[IDX.indexOf(d)]).join(", ") + (full ? "" : " (einzelne Stunden)"));
    }
  }
  if (t?.valid_from || t?.valid_to) parts.push(`${t.valid_from ? `ab ${dateStr(t.valid_from).split("-").reverse().join(".")}` : ""} ${t.valid_to ? `bis ${dateStr(t.valid_to).split("-").reverse().join(".")}` : ""}`.trim());
  return parts.length ? parts.join(" · ") : "immer";
}

/** Inline grid (song editor tab) – the modal below wraps it. */
export function TimingGrid({ value, onSave, onCancel, saveLabel = "Übernehmen" }: { value: Partial<Timing> | null; onSave: (t: Timing) => void | Promise<void>; onCancel?: () => void; saveLabel?: string }) {
  const [grid, setGrid] = useState<number[]>(() => (Array.isArray(value?.schedule) && value!.schedule.length === 7 ? [...value!.schedule] : Array(7).fill(ALL)));
  const [from, setFrom] = useState(dateStr(value?.valid_from));
  const [to, setTo] = useState(dateStr(value?.valid_to));
  const [paint, setPaint] = useState<null | boolean>(null);
  useEffect(() => {
    const up = () => setPaint(null);
    window.addEventListener("mouseup", up);
    return () => window.removeEventListener("mouseup", up);
  }, []);
  const on = (d: number, h: number) => ((grid[d] >>> h) & 1) === 1;
  const set = (d: number, h: number, v: boolean) => setGrid((g) => g.map((x, i) => (i !== d ? x : v ? x | (1 << h) : x & ~(1 << h) & ALL)));
  const preset = (days: number[], hours: (h: number) => boolean = () => true) => {
    let mask = 0;
    for (let h = 0; h < 24; h++) if (hours(h)) mask |= 1 << h;
    setGrid(Array.from({ length: 7 }, (_, d) => (days.includes(d) ? mask : 0)));
  };
  const all = [0, 1, 2, 3, 4, 5, 6];
  const result = (): Timing => ({ schedule: grid.every((x) => x === ALL) ? null : grid, valid_from: from || null, valid_to: to || null });
  return (
    <div>
      <div className="row wrap" style={{ marginBottom: 8 }}>
        <span className="small muted">Schnell:</span>
        <button className="btn small" onClick={() => preset(all)}>Immer</button>
        <button className="btn small" onClick={() => preset([6, 0])}>Nur Wochenende</button>
        <button className="btn small" onClick={() => preset([1, 2, 3, 4, 5])}>Nur Mo–Fr</button>
        <button className="btn small" onClick={() => preset(all, (h) => h >= 6 && h < 18)}>Tag 6–18</button>
        <button className="btn small" onClick={() => preset(all, (h) => h >= 18 || h < 6)}>Nacht 18–6</button>
        <button className="btn small" onClick={() => preset([])}>Alles sperren</button>
      </div>
      <p className="small muted" style={{ marginTop: 0 }}>Grün = darf laufen, rot = gesperrt. Mit gedrückter Maus über Felder ziehen; Klick auf Tag oder Stunde schaltet die ganze Zeile oder Spalte.</p>
      <div style={{ overflowX: "auto", userSelect: "none" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr><th />{Array.from({ length: 24 }, (_, h) => (
              <th key={h} style={{ cursor: "pointer", padding: "0 1px", fontWeight: 500 }} onClick={() => { const v = !IDX.every((d) => on(d, h)); IDX.forEach((d) => set(d, h, v)); }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {IDX.map((d, row) => (
              <tr key={d}>
                <th style={{ cursor: "pointer", paddingRight: 6, textAlign: "left" }} onClick={() => setGrid((g) => g.map((x, i) => (i === d ? (x === ALL ? 0 : ALL) : x)))}>{DAYS[row]}</th>
                {Array.from({ length: 24 }, (_, h) => (
                  <td key={h}
                    onMouseDown={() => { const v = !on(d, h); setPaint(v); set(d, h, v); }}
                    onMouseEnter={() => paint !== null && set(d, h, paint)}
                    style={{ width: 20, height: 20, border: "1px solid var(--line)", background: on(d, h) ? "#5fa877" : "#d9715f", cursor: "pointer" }} />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="form" style={{ marginTop: 10 }}>
        <Field label="Gültig ab (optional)"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="Gültig bis (optional)"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </div>
      <div className="row" style={{ marginTop: 12 }}>
        <span className="small muted">{timingText(result())}</span>
        <span className="spacer" />
        {onCancel && <button className="btn" onClick={onCancel}>Abbrechen</button>}
        <button className="btn primary" onClick={() => onSave(result())}>{saveLabel}</button>
      </div>
    </div>
  );
}

export function TimingEditor({ title, value, onSave, onClose }: { title: string; value: Partial<Timing> | null; onSave: (t: Timing) => void | Promise<void>; onClose: () => void }) {
  return (
    <Modal title={title} onClose={onClose} wide>
      <TimingGrid value={value} onSave={onSave} onCancel={onClose} />
    </Modal>
  );
}
